import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ExternalLink,
  Sparkles,
  Send,
  Globe,
  Share2,
  ArrowUpRight,
  BookOpen,
  Award,
  Calculator,
} from "lucide-react";
import { Logo } from "./Logo";
import { api, type SocialLink } from "../../lib/api";
import { useOptionalStudent } from "../../context/StudentContext";
import { useAuth } from "../../context/AuthContext";

const BOT_USERNAME = (import.meta.env.VITE_BOT_USERNAME || "soorgaa_bot").replace(/^@/, "");

// Safe fallback when the settings list is empty or cannot be loaded: only the bot link.
const DEFAULT_FALLBACK_SOCIALS: SocialLink[] = [
  {
    id: "def-bot",
    platform: "telegram",
    title: "Soorgaa bot in Telegram",
    url: `https://t.me/${BOT_USERNAME}`,
    icon: "telegram",
    isActive: true,
    sortOrder: 1,
  },
];

type RawSocialLink = Partial<SocialLink> & { is_active?: boolean; sort_order?: number };

/** The API sends snake_case (is_active, sort_order). Accept both spellings; keep active, valid http(s) links. */
function normalizeSocials(rows: RawSocialLink[]): SocialLink[] {
  return rows
    .map((r) => ({
      id: String(r.id ?? r.url ?? ""),
      platform: r.platform ?? "link",
      title: r.title ?? "",
      url: r.url ?? "",
      icon: r.icon ?? null,
      isActive: r.isActive ?? r.is_active ?? true,
      sortOrder: r.sortOrder ?? r.sort_order ?? 0,
    }))
    .filter((r) => r.isActive && r.title && /^https?:\/\//i.test(r.url))
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

function renderSocialIcon(platform: string, size = 15) {
  const p = platform.toLowerCase().trim();
  if (p === "telegram" || p === "tg") {
    return <Send size={size} className="text-info shrink-0" />;
  }
  if (p === "youtube" || p === "yt") {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="currentColor"
        className="text-destructive shrink-0"
      >
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
      </svg>
    );
  }
  if (p === "tiktok") {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="currentColor"
        className="text-foreground shrink-0"
      >
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64c.29 0 .58.04.85.12V9.4a6.33 6.33 0 0 0-1-.08A6.34 6.34 0 0 0 3 15.66a6.34 6.34 0 0 0 10.82 4.47 6.28 6.28 0 0 0 1.94-4.52V8.91a8.28 8.28 0 0 0 4.83 1.54V7a4.85 4.85 0 0 1-1-.31z" />
      </svg>
    );
  }
  if (p === "facebook" || p === "fb") {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="currentColor"
        className="text-info shrink-0"
      >
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
      </svg>
    );
  }
  if (p === "instagram" || p === "ig") {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="currentColor"
        className="text-primary shrink-0"
      >
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
      </svg>
    );
  }
  if (p === "x" || p === "twitter") {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="currentColor"
        className="text-foreground shrink-0"
      >
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
    );
  }
  if (p === "linkedin") {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="currentColor"
        className="text-info shrink-0"
      >
        <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
      </svg>
    );
  }
  return <Globe size={size} className="text-primary shrink-0" />;
}

export function Footer({ variant = "landing" }: { variant?: "landing" | "student" }) {
  const [socials, setSocials] = useState<SocialLink[]>(DEFAULT_FALLBACK_SOCIALS);
  const student = useOptionalStudent();
  const { role } = useAuth();
  const isStudent = role === "student";
  const signedIn = Boolean(role);

  useEffect(() => {
    let mounted = true;
    api
      .socialLinks()
      .then((links) => {
        if (!mounted || !Array.isArray(links)) return;
        const clean = normalizeSocials(links as RawSocialLink[]);
        if (clean.length > 0) setSocials(clean);
      })
      .catch(() => {
        // Keep the fallback link
      });
    return () => {
      mounted = false;
    };
  }, []);

  const handleUpgradeClick = (e: React.MouseEvent) => {
    if (student?.openUpgrade) {
      e.preventDefault();
      student.openUpgrade();
    }
  };

  const botUrl = `https://t.me/${BOT_USERNAME}`;
  const link = "hover:text-primary transition inline-flex items-center gap-1.5 min-h-11";
  const heading = "font-display font-bold text-xs uppercase tracking-wide text-foreground flex items-center gap-1.5";

  return (
    <footer data-variant={variant} className="w-full border-t border-border/70 bg-card/60 backdrop-blur-xs text-foreground mt-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-10">
          <div className="space-y-3.5 md:col-span-1">
            <Logo />
            <p className="font-benton text-sm text-muted-foreground leading-relaxed">
              A study website for first-year students in Ethiopian universities.
            </p>
          </div>

          <div className="space-y-3">
            <h4 className={heading}>
              <BookOpen size={13} className="text-primary" aria-hidden="true" />
              <span>Study</span>
            </h4>
            <ul className="text-sm font-benton text-muted-foreground">
              <li>
                <Link to={isStudent ? "/student/study" : "/auth/login"} className={link}>
                  Modules and notes
                </Link>
              </li>
              <li>
                <Link to={isStudent ? "/student/study" : "/auth/login"} className={link}>
                  Past exams
                </Link>
              </li>
              <li>
                <Link to={isStudent ? "/student/coc" : "/auth/login"} className={link}>
                  <Award size={13} className="text-warning" aria-hidden="true" />
                  <span>COC practice</span>
                </Link>
              </li>
              <li>
                <Link to={isStudent ? "/student/study/gpa" : "/auth/login"} className={link}>
                  <Calculator size={13} className="text-primary" aria-hidden="true" />
                  <span>GPA calculator</span>
                </Link>
              </li>
            </ul>
          </div>

          <div className="space-y-3">
            <h4 className={heading}>
              <Sparkles size={13} className="text-primary" aria-hidden="true" />
              <span>Account and help</span>
            </h4>
            <ul className="text-sm font-benton text-muted-foreground">
              <li>
                <Link
                  to={isStudent ? "/student/premium" : "/auth/login?redirect=%2Fstudent%2Fpremium"}
                  onClick={handleUpgradeClick}
                  className={`${link} text-primary font-semibold`}
                >
                  <Sparkles size={13} aria-hidden="true" />
                  <span>Premium</span>
                </Link>
              </li>
              <li>
                <a
                  href={botUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Soorgaa bot in Telegram (opens in a new tab)"
                  className={link}
                >
                  <Send size={13} className="text-info" aria-hidden="true" />
                  <span>Soorgaa bot in Telegram</span>
                  <ExternalLink size={11} className="opacity-70" aria-hidden="true" />
                </a>
              </li>
              {isStudent ? (
                <li>
                  <Link to="/student/profile" className={link}>
                    My profile and receipts
                  </Link>
                </li>
              ) : !signedIn ? (
                <>
                  <li>
                    <Link to="/auth/login" className={link}>
                      Sign in
                    </Link>
                  </li>
                  <li>
                    <Link to="/auth/register" className={link}>
                      Create account
                    </Link>
                  </li>
                </>
              ) : null}
            </ul>
          </div>

          <div className="space-y-3">
            <h4 className={heading}>
              <Share2 size={13} className="text-primary" aria-hidden="true" />
              <span>Follow Soorgaa</span>
            </h4>
            <p className="text-sm font-benton text-muted-foreground">Get updates from Soorgaa.</p>
            <ul className="flex flex-col gap-1.5">
              {socials.map((s) => (
                <li key={s.id || s.url}>
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${s.title} (opens in a new tab)`}
                    className="group flex min-h-11 items-center justify-between gap-2 px-2.5 rounded-xs border border-border/60 bg-secondary/40 hover:bg-secondary hover:border-primary/40 transition text-sm font-benton text-foreground"
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      {renderSocialIcon(s.platform, 15)}
                      <span className="truncate font-semibold group-hover:text-primary transition">{s.title}</span>
                    </span>
                    <ExternalLink size={12} className="text-muted-foreground group-hover:text-primary transition shrink-0" aria-hidden="true" />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="pt-6 border-t border-border/60 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm font-benton text-muted-foreground">
          <span>&copy; {new Date().getFullYear()} Soorgaa</span>
          <div className="flex items-center gap-1 flex-wrap justify-center">
            <a
              href={botUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Soorgaa bot in Telegram (opens in a new tab)"
              className="hover:text-foreground transition inline-flex min-h-11 items-center px-2"
            >
              Soorgaa bot
            </a>
            {!signedIn && (
              <>
                <Link to="/auth/login" className="hover:text-foreground transition inline-flex min-h-11 items-center px-2">
                  Sign in
                </Link>
                <Link to="/auth/register" className="hover:text-foreground transition inline-flex min-h-11 items-center px-2">
                  Create account
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
}
